import React from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import get from "lodash/get";
import { Link, ResponsiveHeroImage, ReviewRating, LazyLoadImages } from "@quintype/components";
import { ImageFallback } from "../image-fallback.js";
import { CollectionName } from "../collection-name.js";

import TimeAgo from "react-timeago";
import "./full-screen-slider.module.css";
import { DotIcon } from "../dot-icon.js";
import { breakpoint } from "../../../../utils/breakpoint";
import { getNumberOfStoriesToShow, altTagOfHeroImages, isExternalStory, formatter, getCarousel } from "../../../utils";
import { computeTimings, markTiming, TIMING_KEYS } from "../../../user-timing";

export function Carousel(props) {
  markTiming(TIMING_KEYS["SLIDER_START"]);
  const {
    metadata: {
      slider_type_dots: sliderTypeDots = false,
      slider_type_dashes: sliderTypeDashes = false,
      set_scroll_speed: scrollSpeed = 500,
      number_of_slides_to_show: slidesToShow = 1,
      number_of_slides_to_scroll: slidesToScroll = 1,
      show_arrows: showArrows = true,
      enable_auto_play: autoplay = true,
      show_collection_name: collectionName = true
    } = {}
  } = props;

  let { metadata: { number_of_stories_to_show: numberOfStoriesToShow } = {} } = props;

  numberOfStoriesToShow = getNumberOfStoriesToShow(numberOfStoriesToShow);

  const slidesOnMobile = breakpoint("mobile") ? 1 : parseInt(slidesToShow);
  const sliderPropObject = {
    dots: sliderTypeDots || sliderTypeDashes,
    speed: scrollSpeed && scrollSpeed > 0 ? scrollSpeed : 500,
    rtl: get(props.config, ["theme-attributes", "language_direction"]) === "right",
    arrows: showArrows,
    slidesToShow: slidesOnMobile && slidesOnMobile > 0 ? slidesOnMobile : 1,
    slidesToScroll: slidesToScroll && parseInt(slidesToScroll) > 0 ? parseInt(slidesToScroll) : 1,
    autoplay: autoplay,
    dotsClass: sliderTypeDashes ? "dashpaging" : "slick-dots",
    lazyLoad: true,
    onLazyLoad: () => {}
  };

  const SliderWrapper = (
    <Slider {...sliderPropObject}>
      {props.stories.slice(0, numberOfStoriesToShow).map(story => {
        return (
          <div key={story.id}>
            <CarouselItem metadata={props.metadata} story={story} config={props.config} collection={props.collection} />
          </div>
        );
      })}
    </Slider>
  );

  const Slide = (
    <CarouselItem
      metadata={props.metadata}
      story={props.stories[0]}
      config={props.config}
      collection={props.collection}
    />
  );

  markTiming(TIMING_KEYS["SLIDER_END"]);

  console.log(computeTimings(["SLIDER_START", "SLIDER_END"]));

  return (
    <div styleName="full-screen-slider-wrapper" data-testid="full-screen-component">
      {collectionName ? <CollectionName name={props.collection.name} type="large" section={props.collection} /> : null}
      <div
        styleName={`full-screen-slider ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "dots-rtl" : ""
        }`}
      >
        {getCarousel(SliderWrapper, Slide)}
      </div>
    </div>
  );
}

Carousel.defaultProps = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  numberOfStoriesToShow: 6,
  lazyLoad: true,
  onLazyLoad: () => {}
};

Carousel.propTypes = {
  stories: PropTypes.array,
  config: PropTypes.object,
  collection: PropTypes.object,
  metadata: PropTypes.object
};

function CarouselItem(props) {
  if (!props.story) {
    return null;
  }
  return (
    <Link href={`/${props.story.slug}`} externalLink={isExternalStory(props.story)}>
      <Slide metadata={props.metadata} config={props.config} story={props.story} collection={props.collection} />
    </Link>
  );
}

CarouselItem.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  collection: PropTypes.object,
  metadata: PropTypes.object
};

function Slide(props) {
  const {
    metadata: {
      show_section_tag: showSectionTag = true,
      show_author_name: showAuthorName = true,
      show_time_of_publish: showTimeOfPublish = true,
      full_width_container: fullWidthContainer = true,
      number_of_slides_to_show: numberOfStoriesToShow = 1
    } = {}
  } = props;
  const aspectRatio = breakpoint("tablet") ? [3, 4] : [3, 1];
  const responsiveFigure = (
    <figure styleName={props.story["story-template"] === "video" ? "video" : ""}>
      <LazyLoadImages margin={"0px"}>
        <ResponsiveHeroImage
          story={props.story}
          aspectRatio={aspectRatio}
          alt={altTagOfHeroImages(props.story)}
          defaultWidth={1024}
          widths={[640, 1024, 1280]}
          sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
          imgParams={{ auto: ["format", "compress"], fit: "max" }}
        />
      </LazyLoadImages>
    </figure>
  );

  const heroImageTemplate = props.story["hero-image-s3-key"] ? responsiveFigure : <ImageFallback />;
  const isReviewStory = props.story["story-template"] === "review";
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];

  return (
    <div
      styleName={`full-screen-slider-slide ${
        fullWidthContainer ? "full-screen-slider-fullWidth" : "full-screen-slider-containerWidth"
      }`}
    >
      <div styleName="slide-image">{heroImageTemplate}</div>
      <div styleName="content">
        {showSectionTag ? (
          <div styleName="section" data-testid="fullscreen-sectionname">
            {get(props.story, ["sections", 0, "display-name"]) || get(props.story, ["sections", 0, "name"], "")}
          </div>
        ) : null}
        <div
          styleName={`title ${numberOfStoriesToShow > 2 ? "title-font-small" : "title-font-big"}`}
          dangerouslySetInnerHTML={{ __html: props.story.headline }}
          data-test-id="full-screen-headline"
        />
        {isReviewStory && <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />}
        <div styleName="created-at">
          {showAuthorName ? (
            <div data-testid="fullscreen-authorname">
              {" "}
              BY {get(props.story, ["authors", 0, "name"], props.story["author-name"])}{" "}
            </div>
          ) : null}
          {showAuthorName && showTimeOfPublish ? <DotIcon fill="#fff" /> : null}
          {showTimeOfPublish ? (
            <div data-testid="fullscreen-showtime">
              <TimeAgo
                date={publishedTime}
                formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date, props.config)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

Slide.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  metadata: PropTypes.object
};
